import { useState, useEffect, useMemo, useCallback } from 'react';
import { sortByName, sortByTagId } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { urlRegistryAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Link2,
    Search,
    Copy,
    Check,
    ExternalLink,
    Filter,
    CreditCard,
    MapPin,
    Building2,
    Globe,
    ChevronDown,
    Download,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';

const PLATFORM_LABELS = {
    google_reviews: 'Google',
    facebook: 'Facebook',
    instagram: 'Instagram',
    trustpilot: 'Trustpilot',
    yelp: 'Yelp',
};

const STATUS_STYLES = {
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    assigned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    unassigned: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    inactive: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const CopyButton = ({ text, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success(`${label} copied`);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Failed to copy');
        }
    };

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopy}
                        className="h-8 gap-1.5 text-xs font-medium"
                        data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}-btn`}
                    >
                        {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                        {label}
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="text-xs max-w-xs break-all">{text}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default function URLRegistry() {
    const { t } = useTranslation();
    const { user, isSuperAdmin, isClientAdmin } = useAuth();
    // v4.34.2 — Capture role as a stable primitive so loadData's deps are
    // referentially stable (the isSuperAdmin function from useAuth is
    // recreated on every render and would cause an infinite refetch loop
    // if added to a useCallback / useEffect dependency array).
    const isSuper = user?.role === 'super_admin';
    const [cards, setCards] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [orgFilter, setOrgFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [platformFilter, setPlatformFilter] = useState('all');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [cardsRes, orgsRes] = await Promise.all([
                urlRegistryAPI.list(),
                isSuper ? organizationsAPI.list() : Promise.resolve({ data: [] }),
            ]);
            setCards(sortByTagId(cardsRes.data));
            if (isSuper) {
                setOrganizations(sortByName(orgsRes.data));
            }
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to load URL Registry';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [isSuper]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const filtered = useMemo(() => {
        return cards.filter((c) => {
            const q = search.toLowerCase();
            const matchesSearch =
                !q ||
                c.card_id?.toLowerCase().includes(q) ||
                c.name?.toLowerCase().includes(q) ||
                c.organization_name?.toLowerCase().includes(q) ||
                c.location_name?.toLowerCase().includes(q) ||
                c.employee_name?.toLowerCase().includes(q);
            const matchesOrg = orgFilter === 'all' || c.organization_id === orgFilter;
            const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
            const matchesPlatform = platformFilter === 'all' || c.platform === platformFilter;
            return matchesSearch && matchesOrg && matchesStatus && matchesPlatform;
        });
    }, [cards, search, orgFilter, statusFilter, platformFilter]);

    const exportBulkCSV = () => {
        if (filtered.length === 0) {
            toast.error('No data to export');
            return;
        }
        const header = ['Tag ID', 'Name', 'Organization', 'Location', 'Platform', 'Status', 'Employee', 'Landing Enabled', 'Tap URL', 'Landing URL'];
        const rows = filtered.map((c) => [
            c.card_id,
            c.name || '',
            c.organization_name || '',
            c.location_name || '',
            c.platform,
            c.status,
            c.employee_name || '',
            c.landing_page_enabled ? 'Yes' : 'No',
            c.tap_url,
            c.landing_page_enabled ? c.landing_url : '',
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

        const csv = [header.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `YulOnTap_URL_Registry_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${filtered.length} tags to CSV`);
    };

    if (!isSuperAdmin() && !isClientAdmin()) {
        return (
            <div className="p-6 text-center text-muted-foreground" data-testid="url-registry-no-access">
                You do not have access to the URL Registry.
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="url-registry-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Link2 size={24} className="text-blue-600" />
                        URL Registry
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('urlRegistry.subtitle', { defaultValue: 'View and copy Tap URLs and Landing Page URLs for all tags' })}</p>
                </div>
                {isSuperAdmin() && (
                    <Button
                        variant="outline"
                        onClick={exportBulkCSV}
                        disabled={filtered.length === 0}
                        data-testid="bulk-export-csv-btn"
                    >
                        <Download size={16} className="mr-2" />
                        Export All URLs
                    </Button>
                )}
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-4 pb-4">
                    <div className="flex flex-wrap gap-3 items-center">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                            <Input
                                placeholder="Search by Tag ID, name, org, location, employee..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                                data-testid="url-registry-search"
                            />
                        </div>
                        {isSuperAdmin() && organizations.length > 0 && (
                            <Select value={orgFilter} onValueChange={setOrgFilter}>
                                <SelectTrigger className="w-[200px]" data-testid="url-registry-org-filter">
                                    <Building2 size={14} className="mr-1.5 text-muted-foreground" />
                                    <SelectValue placeholder={t('urlRegistry.organization')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('locationHealth.allOrganizations')}</SelectItem>
                                    {organizations.map((o) => (
                                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[150px]" data-testid="url-registry-status-filter">
                                <SelectValue placeholder={t('urlRegistry.status')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="assigned">Assigned</SelectItem>
                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={platformFilter} onValueChange={setPlatformFilter}>
                            <SelectTrigger className="w-[150px]" data-testid="url-registry-platform-filter">
                                <Globe size={14} className="mr-1.5 text-muted-foreground" />
                                <SelectValue placeholder={t('urlRegistry.platform')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Platforms</SelectItem>
                                {Object.entries(PLATFORM_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Summary */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground" data-testid="url-registry-count">
                    Showing <span className="font-semibold text-slate-900 dark:text-white">{filtered.length}</span> of {cards.length} tags
                </p>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-48" data-testid="url-registry-loading">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-muted-foreground" data-testid="url-registry-empty">
                            <CreditCard size={40} className="mb-3 opacity-40" />
                            <p className="font-medium text-foreground mb-1">{t('empty.urlRegistryEmpty', 'No tags found')}</p>
                            <p className="text-sm">{t('empty.urlRegistryEmptyDesc', 'There are no tags matching your current filters.')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                                        <TableHead className="font-semibold">Tag ID</TableHead>
                                        {isSuperAdmin() && <TableHead className="font-semibold">Organization</TableHead>}
                                        <TableHead className="font-semibold">Location</TableHead>
                                        <TableHead className="font-semibold">Platform</TableHead>
                                        <TableHead className="font-semibold" data-testid="url-registry-source-header">{t('url_registry.source_column')}</TableHead>
                                        <TableHead className="font-semibold">Status</TableHead>
                                        <TableHead className="font-semibold">Employee</TableHead>
                                        <TableHead className="font-semibold text-center">Tap URL</TableHead>
                                        <TableHead className="font-semibold text-center">Landing URL</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((card) => (
                                        <TableRow key={card.id} data-testid={`url-registry-row-${card.card_id}`}>
                                            <TableCell className="font-mono font-semibold text-sm">
                                                {card.card_id}
                                                {card.name && (
                                                    <span className="block text-xs text-muted-foreground font-normal">{card.name}</span>
                                                )}
                                            </TableCell>
                                            {isSuperAdmin() && (
                                                <TableCell className="text-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <Building2 size={13} className="text-muted-foreground shrink-0" />
                                                        <span className="truncate max-w-[140px]">{card.organization_name || '-'}</span>
                                                    </div>
                                                </TableCell>
                                            )}
                                            <TableCell className="text-sm">
                                                <div className="flex items-center gap-1.5">
                                                    <MapPin size={13} className="text-muted-foreground shrink-0" />
                                                    <span className="truncate max-w-[140px]">{card.location_name || '-'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="text-xs font-medium">
                                                    {PLATFORM_LABELS[card.platform] || card.platform}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {/* v4.31.0 — Source attribution badge: location / organization / platform_values / default. */}
                                                <Badge
                                                    variant="outline"
                                                    className={`text-[10px] font-medium uppercase tracking-wide ${
                                                        card.redirect_source === 'location'
                                                            ? 'border-fuchsia-300 text-fuchsia-700 dark:border-fuchsia-700 dark:text-fuchsia-300'
                                                            : card.redirect_source === 'organization'
                                                            ? 'border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300'
                                                            : card.redirect_source === 'platform_values'
                                                            ? 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300'
                                                            : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400'
                                                    }`}
                                                    data-testid={`url-registry-source-${card.card_id}`}
                                                    title={card.redirect_url || ''}
                                                >
                                                    {t(`url_registry.source.${card.redirect_source || 'default'}`)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={`text-xs capitalize ${STATUS_STYLES[card.status] || STATUS_STYLES.unassigned}`}>
                                                    {card.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {card.employee_name || '-'}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <CopyButton text={card.tap_url} label="Tap URL" />
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {card.landing_page_enabled ? (
                                                    <CopyButton text={card.landing_url} label="Landing URL" />
                                                ) : (
                                                    <span className="text-xs text-muted-foreground italic">{t('urlRegistry.notEnabled')}</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
